import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DestinyTeamOption, TeamPublic, UserPublic } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import { api } from '@/api';
import { Spinner } from '@/components/Spinner';
import { TeamFlag } from '@/components/TeamFlag';

/**
 * Constants for the slot-machine animation. The viewport shows a fixed number
 * of rows, with the center row highlighted. The reel is `CYCLES` copies of the
 * teams list concatenated so there's plenty of runway to scroll past.
 *
 * Sizing switches at the codebase-wide 820px shell breakpoint so the reveal
 * fits on phone viewports without scrolling (hero + reel + CTA all visible).
 */
const ROW_HEIGHT_DESKTOP = 64;
const ROW_HEIGHT_MOBILE = 56;
const VISIBLE_ROWS = 5;
const VIEWPORT_HEIGHT_DESKTOP = ROW_HEIGHT_DESKTOP * VISIBLE_ROWS;
const VIEWPORT_HEIGHT_MOBILE = ROW_HEIGHT_MOBILE * VISIBLE_ROWS;
const MOBILE_MQ = '(max-width: 820px)';
const CYCLES = 8;
const SPIN_DURATION_MS = 3200;

type GateState =
  | { kind: 'hero' }
  | { kind: 'spinning' }
  | { kind: 'reveal'; team: TeamPublic }
  | { kind: 'saving'; team: TeamPublic }
  | { kind: 'error'; team: TeamPublic; message: string };

/**
 * The destiny wheel. Used in two places:
 *  - first-time signup (rendered from App.tsx when the user has no favorite
 *    team yet) — omits `onSuccess`, so the default invalidate of `['auth','me']`
 *    cascades into App.tsx flipping back to the normal shell.
 *  - reroll from Profile (rendered by the `/reroll` route) — passes an
 *    `onSuccess` that navigates back to `/profile` after the save lands.
 *
 * The server returns each team together with a `pickedBy` count (other users
 * who already have that team favorited). The reel renders uniformly, but the
 * winner is drawn with weights of `1 / (1 + pickedBy)` so popular teams are
 * down-weighted without ever being eliminated.
 */
export function FavoriteTeamGate({ onSuccess }: { onSuccess?: () => void } = {}) {
  const qc = useQueryClient();
  const teamsQuery = useQuery({
    queryKey: ['teams', 'available'],
    queryFn: () => api.get<{ teams: DestinyTeamOption[] }>('/api/user/available-teams'),
    staleTime: 60 * 1000,
  });

  const options = teamsQuery.data?.teams ?? [];

  const save = useMutation({
    mutationFn: (id: string) =>
      api.put<{ user: UserPublic }>('/api/user/favorite-team', { teamId: id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      onSuccess?.();
    },
  });

  return (
    <main className="app-main destiny-gate" style={{ minHeight: '100dvh' }}>
      <div className="destiny-gate-inner">
        {teamsQuery.isLoading ? (
          <Spinner fullScreen label="Loading teams" />
        ) : teamsQuery.isError ? (
          <p style={{ color: 'var(--danger)', fontSize: 13 }}>Couldn't load teams.</p>
        ) : options.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
            No teams available — ask an admin.
          </p>
        ) : (
          <DestinySpinner options={options} save={save} />
        )}
      </div>
    </main>
  );
}

function DestinySpinner({
  options,
  save,
}: {
  options: DestinyTeamOption[];
  save: ReturnType<typeof useMutation<{ user: UserPublic }, Error, string>>;
}) {
  const [state, setState] = useState<GateState>({ kind: 'hero' });
  const reelRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isMobile = useMediaQuery(MOBILE_MQ);
  const rowHeight = isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP;
  const viewportHeight = isMobile ? VIEWPORT_HEIGHT_MOBILE : VIEWPORT_HEIGHT_DESKTOP;

  // The visible reel renders teams uniformly — the pickedBy bias only
  // influences winner selection, never the reel ordering. Keeping the visual
  // uniform means watchers don't see which teams are "heavier" or "lighter".
  const allTeams = useMemo(() => options.map((o) => o.team), [options]);

  // Shuffle once so the reel order is unique per visit, then repeat the
  // shuffled list so the reel has enough runway to scroll past convincingly.
  const reel = useMemo(() => {
    const shuffled = shuffle(allTeams);
    const out: TeamPublic[] = [];
    for (let i = 0; i < CYCLES; i++) out.push(...shuffled);
    return out;
  }, [allTeams]);

  function autoSave(winner: TeamPublic) {
    setState({ kind: 'saving', team: winner });
    save.mutate(winner.id, {
      onError: (err) => {
        setState({
          kind: 'error',
          team: winner,
          message: err instanceof ApiError ? err.message : 'Save failed',
        });
      },
    });
  }

  function spin() {
    if (state.kind !== 'hero' || options.length === 0) return;

    const winner = pickWeighted(options);

    // Find the index of the winner within the reel — land in one of the later
    // cycles so the reel has visible travel before stopping.
    // `reel[i]` corresponds to the shuffled team list at `i % allTeams.length`;
    // we pick a landing cycle near the end of the reel.
    const landingCycle = CYCLES - 2; // second-to-last cycle
    const shuffledIdx = reel.findIndex(
      (t, i) => t.id === winner.id && Math.floor(i / allTeams.length) === landingCycle,
    );
    // Fallback: shouldn't happen, but just in case the findIndex misses.
    const landingIdx =
      shuffledIdx >= 0 ? shuffledIdx : reel.findIndex((t) => t.id === winner.id);

    // Compute the final translateY so `landingIdx` is centered in the viewport.
    const centerOffset = viewportHeight / 2 - rowHeight / 2;
    const finalTranslate = -(landingIdx * rowHeight - centerOffset);

    const reel$ = reelRef.current;
    if (!reel$) return;

    setState({ kind: 'spinning' });

    if (prefersReducedMotion) {
      // No animation — snap to the winner after a short beat so the UI still
      // feels intentional rather than jarring.
      reel$.style.transition = 'none';
      reel$.style.transform = `translateY(${finalTranslate}px)`;
      window.setTimeout(() => autoSave(winner), 250);
      return;
    }

    // Start position: above the landing position by enough rows to cover the
    // full spin. We'll use a cubic-bezier ease-out that starts fast and decays
    // smoothly — matches a slot machine coming to rest on the winning symbol.
    const startTranslate = finalTranslate + rowHeight * allTeams.length * (CYCLES - landingCycle);

    // Prime the initial position without a transition, then in the next frame
    // enable the transition and animate to the final position. Two RAFs ensure
    // the browser paints the start state before the transition kicks in.
    reel$.style.transition = 'none';
    reel$.style.transform = `translateY(${startTranslate}px)`;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        reel$.style.transition = `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.16, 0.84, 0.25, 1)`;
        reel$.style.transform = `translateY(${finalTranslate}px)`;
      });
    });

    window.setTimeout(() => autoSave(winner), SPIN_DURATION_MS + 60);
  }

  function lockIn() {
    // Locking happens automatically when the spin ends — the button is kept
    // for the visual payoff but clicking it is a no-op.
  }

  const isHero = state.kind === 'hero';
  const isSpinning = state.kind === 'spinning';
  const isReveal = state.kind === 'reveal' || state.kind === 'saving' || state.kind === 'error';
  const revealTeam =
    state.kind === 'reveal' || state.kind === 'saving' || state.kind === 'error'
      ? state.team
      : null;
  const saving = state.kind === 'saving';
  const errorMessage = state.kind === 'error' ? state.message : null;

  return (
    <div
      className="destiny-gate-card"
      style={{
        width: '100%',
        maxWidth: 560,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 28,
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div
          className="destiny-eyebrow"
          style={{
            fontSize: 11,
            letterSpacing: '0.28em',
            color: 'var(--fg-muted)',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          One last thing
        </div>
        <h1
          className="bebas destiny-headline"
          style={{
            margin: '14px 0 0',
            fontSize: 'clamp(40px, 7vw, 64px)',
            lineHeight: 1.02,
            letterSpacing: '0.02em',
          }}
        >
          Your <span style={{ color: 'var(--hot)' }}>team of destiny</span>
        </h1>
        <p
          className="destiny-subtitle"
          style={{
            marginTop: 14,
            fontSize: 14,
            lineHeight: 1.55,
            color: 'var(--fg-muted)',
            maxWidth: 440,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {isHero
            ? 'The football gods will decide. Spin to discover your team for World Cup 2026.'
            : isSpinning
              ? 'Fate is spinning…'
              : 'The gods have spoken. This is your team for World Cup 2026.'}
        </p>
      </div>

      {isReveal && revealTeam ? (
        <div
          className="destiny-reveal-label"
          style={{
            textAlign: 'center',
            fontSize: 11,
            letterSpacing: '0.24em',
            color: 'var(--fg-muted)',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          Your team: <span style={{ color: 'var(--fg)' }}>{revealTeam.name}</span>
        </div>
      ) : null}

      <div
        className={`destiny-reel ${isReveal ? 'landed' : ''}`}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 420,
          height: viewportHeight,
          overflow: 'hidden',
          borderRadius: 18,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
          maskImage:
            'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
        }}
      >
        <div
          ref={reelRef}
          style={{
            willChange: 'transform',
            // Initial position centers the first row.
            transform: `translateY(${viewportHeight / 2 - rowHeight / 2}px)`,
          }}
        >
          {reel.map((t, i) => (
            <ReelRow key={`${t.id}-${i}`} team={t} />
          ))}
        </div>

        {/* Center highlight overlay — fixed in the viewport, independent of the
            reel's translateY. */}
        <div
          aria-hidden
          className={`destiny-slot ${isReveal ? 'landed' : ''}`}
          style={{
            position: 'absolute',
            left: 8,
            right: 8,
            top: viewportHeight / 2 - rowHeight / 2,
            height: rowHeight,
            borderRadius: 12,
            pointerEvents: 'none',
          }}
        />
      </div>

      {errorMessage ? (
        <p
          style={{
            fontSize: 12,
            color: 'var(--danger)',
            textAlign: 'center',
            margin: 0,
          }}
        >
          {errorMessage}
        </p>
      ) : null}

      <div style={{ width: '100%', maxWidth: 420 }}>
        {isHero ? (
          <button type="button" className="btn btn-primary btn-full" onClick={spin}>
            Spin the wheel
          </button>
        ) : isSpinning ? (
          <button type="button" className="btn btn-primary btn-full" disabled>
            Spinning…
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-full"
            disabled={saving}
            onClick={lockIn}
          >
            {saving ? 'Saving…' : 'Lock it in'}
          </button>
        )}
      </div>

      {/*
        Scoped styles for the reel. Injected here so the component is
        self-contained and index.css stays focused on global tokens.
      */}
      <style>{`
        .destiny-gate-inner {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 80dvh;
          padding:
            max(24px, env(safe-area-inset-top))
            max(16px, env(safe-area-inset-right))
            max(24px, env(safe-area-inset-bottom))
            max(16px, env(safe-area-inset-left));
        }
        .destiny-reel .destiny-row {
          display: flex;
          align-items: center;
          gap: 14px;
          height: ${ROW_HEIGHT_DESKTOP}px;
          padding: 0 22px;
          font-size: 16px;
          font-weight: 500;
          color: var(--fg);
        }
        .destiny-reel .destiny-row .code {
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.1em;
          color: var(--fg-muted);
          min-width: 40px;
        }
        .destiny-reel .destiny-row .name {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family: var(--font-display);
          font-size: 22px;
          letter-spacing: 0.03em;
        }
        .destiny-slot {
          border: 1px solid var(--line);
          background: transparent;
          transition: border-color 320ms ease, box-shadow 320ms ease, background 320ms ease;
        }
        .destiny-slot.landed {
          border-color: var(--hot);
          background: var(--hot-soft);
          box-shadow:
            0 0 0 1px var(--hot-soft),
            0 0 24px 2px var(--hot-soft);
          animation: destiny-pulse 1.8s ease-in-out infinite;
        }
        @keyframes destiny-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px var(--hot-soft),
              0 0 18px 2px var(--hot-soft);
          }
          50% {
            box-shadow:
              0 0 0 2px var(--hot-soft),
              0 0 34px 6px var(--hot-soft);
          }
        }

        /* ---- Narrow viewports (matches the codebase-wide 820px shell breakpoint) ---- */
        @media (max-width: 820px) {
          .destiny-gate-inner {
            padding:
              max(16px, env(safe-area-inset-top))
              max(14px, env(safe-area-inset-right))
              max(16px, env(safe-area-inset-bottom))
              max(14px, env(safe-area-inset-left));
          }
          .destiny-headline {
            font-size: clamp(28px, 8vw, 40px) !important;
            margin-top: 10px !important;
          }
          .destiny-subtitle {
            font-size: 14px !important;
            margin-top: 10px !important;
          }
          .destiny-eyebrow,
          .destiny-reveal-label {
            font-size: 10px !important;
            letter-spacing: 0.22em !important;
          }
          .destiny-gate-card {
            gap: 18px !important;
          }
          .destiny-reel .destiny-row {
            height: ${ROW_HEIGHT_MOBILE}px;
            padding: 0 16px;
            gap: 10px;
          }
          .destiny-reel .destiny-row .name {
            font-size: 18px;
          }
          .destiny-reel .destiny-row .code {
            min-width: 34px;
          }
        }

        /* iPhone SE class — cap the button width but keep full-bleed feel. */
        @media (max-width: 380px) {
          .destiny-reel .destiny-row {
            padding: 0 12px;
            gap: 8px;
          }
          .destiny-reel .destiny-row .name {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
}

function ReelRow({ team }: { team: TeamPublic }) {
  return (
    <div className="destiny-row">
      <TeamFlag code={team.fifaCode} name={team.name} url={team.flagUrl} size="lg" />
      <span className="code">{team.fifaCode}</span>
      <span className="name">{team.name}</span>
    </div>
  );
}

/**
 * Weighted winner draw: each option's weight is `1 / (1 + pickedBy)`, which
 * down-weights already-popular teams without eliminating any. If every team
 * has `pickedBy === 0` the weights collapse to 1 and the draw is uniform.
 */
function pickWeighted(opts: readonly DestinyTeamOption[]): TeamPublic {
  const weights = opts.map((o) => 1 / (1 + Math.max(0, o.pickedBy)));
  const total = weights.reduce((sum, w) => sum + w, 0);
  let r = Math.random() * total;
  for (let i = 0; i < opts.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return opts[i]!.team;
  }
  // Floating-point slop fallback — return the last option.
  return opts[opts.length - 1]!.team;
}

/** Fisher–Yates, non-mutating. */
function shuffle<T>(input: readonly T[]): T[] {
  const arr = input.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}

function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}
